import * as React from 'react'
import { Form } from 'antd'
import { useImmer } from 'use-immer'
import { get, set, cloneDeep, isEmpty, pick } from 'lodash'
import { setStorageItem, getStorageItem } from 'helpers/localStorage'
import { showError, showClientNotifications } from 'helpers/errors'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import Modal from 'elements/Modal'
import Checkbox from 'elements/Checkbox'
import Drawer from 'elements/Drawer'

const getStorageKey = () => 'requisitions.convertToRfq'

const CHECKBOXES = [
  'setQuantityToUnissuedQuantity',
  'addRequisitionDocumentsToRfq',
  'closeRequisitionAfterConvertingToRfq',
]

const RfqForm = linkTargets.rfqRecord.formComponent

function Component(props) {
  const { user, parentRecord, selectedRowKeys } = props

  const selectedRequisitionItems = (parentRecord?.requisitionItems ?? []).filter((each) =>
    selectedRowKeys.includes(each.id)
  )

  const [state, updateState] = useImmer({ modalVisible: true, ...getStorageItem(getStorageKey()) })

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  async function handleModalOk() {
    try {
      setState('modalOkButtonLoading', true)

      const responses = await Promise.all([
        props.newRfq(),
        props.populateRfqItems(
          selectedRequisitionItems
            .map((each) => ({
              ...each,
              userName: user.userName,
              quantity: state.setQuantityToUnissuedQuantity
                ? each.quantity - each.quantityIssued
                : each.quantity,
              requisitionId: undefined,
              requisitionItemId: undefined,
            }))
            .filter((each) => each.quantity > 0)
        ),
      ])

      const item = cloneDeep(responses[0].value.data)

      const rfqItems = get(responses[1], 'value.data.items', []).map((each) => ({
        ...each,
        sourceId: `REQ${parentRecord.id}`,
        sourceItemId: each.id,
        id: undefined,
        rfqItemId: undefined,
        timeScanned: new Date().toJSON(),
        serverTimeScanned: new Date().toJSON(),
      }))

      const response = await props.saveRfq({
        rfq: item,
        rfqItems: { creating: rfqItems },
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const rfq = cloneDeep(response.value.data.items[0])

      rfq.sourceRequisitionId = parentRecord.id

      if (state.addRequisitionDocumentsToRfq) {
        const copied = await props.copyAllObjectDocuments({
          sourceRequest: {
            domainObjectId: parentRecord.id,
            domainObjectType: 'Requisition',
          },
          destinationRequest: {
            domainObjectId: rfq.id,
            domainObjectType: 'Rfq',
          },
        })

        showClientNotifications({ response: copied })

        if (copied.value.data.failureCount > 0) {
          throw new Error()
        }
      }

      updateState((draft) => {
        draft.modalVisible = false
        draft.drawerVisible = true
        draft.drawerSaving = false
        draft.linkTargetRecord = { ...rfq, rfqId: rfq.id }
      })

      props.onCreateRfq(pick(state, ['closeRequisitionAfterConvertingToRfq']))
    } catch (error) {
      showError({ error })
    } finally {
      setState('modalOkButtonLoading', false)
    }
  }

  function handleRfqFormClose() {
    updateState((draft) => {
      draft.modalVisible = false
      draft.drawerVisible = false
    })

    window.setTimeout(props.onCreatedRfqDrawerClose, 500)
  }

  React.useEffect(() => {
    setStorageItem(
      getStorageKey(),
      CHECKBOXES.reduce((acc, each) => ({ ...acc, [each]: state[each] }), {})
    )
  }, [state])

  return (
    <>
      <Modal
        title={t('convertToRfq')}
        visible={state.modalVisible && !isEmpty(selectedRequisitionItems)}
        okText={t('convert')}
        onOk={handleModalOk}
        okButtonProps={{ loading: state.modalOkButtonLoading }}
        onCancel={props.onCancel}
      >
        {CHECKBOXES.map((key) => (
          <Form.Item key={key}>
            <Checkbox
              checked={get(state, key)}
              onChange={(e) =>
                updateState((draft) => {
                  set(draft, key, e.target.checked)
                })
              }
            >
              {t(key)}
            </Checkbox>
          </Form.Item>
        ))}
      </Modal>
      <Drawer
        title={t('createRfq')}
        size={linkTargets.rfqRecord.formSize}
        visible={state.drawerVisible}
        onClose={handleRfqFormClose}
        saving={state.drawerSaving}
      >
        <RfqForm
          linkTargetRecord={state.linkTargetRecord}
          onSave={(pending) => setState('drawerSaving', pending)}
          onSaveAndClose={handleRfqFormClose}
          onCancel={handleRfqFormClose}
        />
      </Drawer>
    </>
  )
}

export default Component
